import { create } from 'zustand';
import { STORAGE_KEYS } from '@/lib/constants';

export type ThemeMode = 'system' | 'light' | 'dark';

const VALID_THEMES: ThemeMode[] = ['system', 'light', 'dark'];

function getStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.THEME);
    return VALID_THEMES.includes(stored as ThemeMode) ? (stored as ThemeMode) : 'system';
  } catch {
    return 'system';
  }
}

function getSystemTheme(): Exclude<ThemeMode, 'system'> {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeToDOM(theme: ThemeMode) {
  const root = document.documentElement;
  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  root.setAttribute('data-theme-mode', theme);
  root.setAttribute('data-theme', resolvedTheme);
}

interface ThemeState {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  initTheme: () => () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: getStoredTheme(),
  setTheme: (theme) => {
    const valid = VALID_THEMES.includes(theme) ? theme : 'system';
    try {
      localStorage.setItem(STORAGE_KEYS.THEME, valid);
    } catch {
      // localStorage unavailable
    }
    applyThemeToDOM(valid);
    set({ theme: valid });
  },
  initTheme: () => {
    const theme = get().theme;
    applyThemeToDOM(theme);

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (get().theme === 'system') {
        applyThemeToDOM('system');
      }
    };
    mql.addEventListener('change', handler);

    // Return cleanup function
    return () => mql.removeEventListener('change', handler);
  },
}));
